import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { StyleClassModule } from 'primeng/styleclass';
import { combineLatest, Subscription } from 'rxjs';
import { UserMenuItemComponent } from 'src/app/shared/components/menu-components/user-menu-item/user-menu-item.component';
import { SelectableOrgsDropdownComponent } from 'src/app/shared/components/selectable-orgs-dropdown/selectable-orgs-dropdown.component';
import { RoleType } from 'src/app/shared/enums/user/role-types.enum';
import { IOrganization } from 'src/app/shared/models/organization/organization.model';
import { ContainsAnyPipe } from 'src/app/shared/pipes/contains-any.pipe';
import { SelectedOrgService } from 'src/app/shared/services/selected-org.service';
import { UserStoreService } from 'src/app/shared/services/store/user-store.service';
import { getAdminMenuItems, IAdminMenuItem } from './admin-menu-items';
import { DeviceSize, SystemService } from 'src/app/shared/services/system.service';
import { AdminLayoutService } from '../admin-layout.service';
import { TranslateModule } from '@ngx-translate/core';
import { IModalFooterMenuItem, MODAL_FOOTER_MENU_SPACER_CONTAINER_SELECTOR, ModalFooterMenuComponent } from 'src/app/shared/components/modal-footer-menu/modal-footer-menu.component';
import { StoreService } from 'src/app/shared/services/store/store.service';
import { ModalService } from 'src/app/shared/services/modal.service';
import { SessionLecturerAttendanceModalComponent } from 'src/app/shared/modals/session-lecturer-attendance-modal/session-lecturer-attendance-modal.component';
import { SessionLecturerType } from 'src/app/shared/enums/session-group/session-lecturer-types.enum';

@Component({
  selector: 'app-admin-layout-menu',
  standalone: true,
  imports: [
    CommonModule, RouterModule,
    StyleClassModule, TranslateModule,
    SelectableOrgsDropdownComponent, UserMenuItemComponent, ModalFooterMenuComponent,
  ],
  templateUrl: './admin-layout-menu.component.html',
  styleUrls: ['./admin-layout-menu.component.scss'],
})
export class AdminLayoutMenuComponent implements OnInit, AfterViewInit, OnDestroy {
  ADMIN_MENU_ITEMS = getAdminMenuItems(this.store);
  DeviceSize = DeviceSize;
  MODAL_FOOTER_MENU_SPACER_CONTAINER_SELECTOR = MODAL_FOOTER_MENU_SPACER_CONTAINER_SELECTOR;

  id="app-admin-menu";

  @ViewChild('menu') menu: ElementRef | undefined;
  @ViewChild('backdrop') backdrop: ElementRef | undefined;

  showMenuForRoles: RoleType[] = [];
  userRoleTypes: RoleType[] = [];

  organizations: IOrganization[] = [];

  fetchingOrganizations: boolean = false;

  adminMenuItems: IAdminMenuItem[] = [];
  quickMobileMenuItems: IModalFooterMenuItem[] = [];

  compact: boolean = false;
  small: boolean = false;
  smallBreakpoint: number = 992;

  subs: Subscription[] = [];

  constructor(
    private selectedOrgService: SelectedOrgService,
    private userStore: UserStoreService,
    public sysService: SystemService,
    private adminLayoutService: AdminLayoutService,
    private router: Router,
    private route: ActivatedRoute,
    private store: StoreService,
    private modalService: ModalService,
  ) {
    this.subs.push(
      combineLatest(this.selectedOrgService.selectedOrgId$, this.userStore.state.user$).subscribe(v => {
        const orgId = v[0];
        const user = v[1];

        this.userRoleTypes = user?.roles.map(x => x.type) ?? [];

        if (orgId && user) {
          this.showMenuForRoles = user.roles.filter(x => x.organizationId === orgId).map(x => x.type);
        } else {
          this.showMenuForRoles = [];
        }

        if (user?.roles.find(x => x.type === RoleType.SYSTEM_ADMIN)) this.showMenuForRoles.push( RoleType.SYSTEM_ADMIN );

        this.initAdminMenuItems();
      }),

      this.sysService.deviceWidth$.subscribe(width => {
        this.small = width < this.smallBreakpoint;
      }),

      this.adminLayoutService.secondaryContentAttached$.subscribe(attached => {
        this.compact = attached;
      }),

      this.store.actions.adminMenu_openSubstituteModal$.subscribe(() => {
        this.modalService.openSessionLecturerAttendanceModal(SessionLecturerAttendanceModalComponent, null, SessionLecturerType.SUBSTITUTION);
      })
    );
  }

  ngOnInit(): void {
    
  }

  ngAfterViewInit(): void {
    // watch menu element changes, if visible, show backdrop, if not, hide backdrop
    if (this.menu) {
      const menuElement = this.menu.nativeElement as HTMLElement;
      const observer = new MutationObserver(() => {
        if (menuElement.classList.contains('hidden')) {
          this.backdrop?.nativeElement.classList.add('hidden');
        } else {
          this.backdrop?.nativeElement.classList.remove('hidden');
        }
      });
      observer.observe(menuElement, { attributes: true, attributeFilter: ['class'] });
    }
  }

  private initAdminMenuItems() {
    const items: IAdminMenuItem[] = [];
    for (const item of this.ADMIN_MENU_ITEMS) {
      if (
        item.showForAll ||
        this.showMenuForRoles.some(r => item.showFor?.includes(r)) ||
        (item.showHasAnywhere && this.userRoleTypes.includes(item.showHasAnywhere))
      ) {
        items.push(item);
      }
    }
    this.adminMenuItems = items;
    this.quickMobileMenuItems = items.filter(x => x.isQuickMobileItem).map(x => ({
      icon: x.icon,
      translationText: x.title,
      action: () => {
        if (x.path) this.router.navigate([x.path], { relativeTo: this.route });
        this.onMenuItem(x);
      },
      isActive: () => x.path ? this.router.isActive(this.router.createUrlTree([x.path], { relativeTo: this.route }), true) : false,
    }))
  }

  onMenuBackdrop(e: Event) {
    e.stopPropagation();

    const menu = document.getElementById(this.id)
    menu?.classList.add('hidden');
  }

  onMenuItem(item: IAdminMenuItem) {
    if (item.action) item.action();
    const menu = document.getElementById(this.id)
    menu?.classList.add('hidden');
  }

  ngOnDestroy(): void {
    this.subs.forEach(x => x.unsubscribe());
  }

}
